import { useState, useEffect } from 'react';
import { tenantsAPI } from '@/api/api';
import { useAuth } from '@/context/AuthContext';

// Fixed modules - always available for Admin
export const FIXED_MODULES = [
  'dashboard',
  'profile',
  'college-settings',
  'master-settings',
  'consultants',
  'seat-booking',
  'admission',
  'fees',
  'accounts',
  'reports',
  'tickets'
];

// Configurable modules
export const CONFIGURABLE_MODULES = [
  'attendance',
  'routine',
  'examination',
  'hr',
  'leave',
  'library',
  'inventory',
  'hostel',
  'transport',
  'communication',
  'transcripts',
  'user-management',
  'roles',
  'frontdesk',
  'cash-register',
  'bank-register',
  'placement',
  'lms'
];

// Module to menu mapping
export const MODULE_MENU_MAPPING = {
  'dashboard': ['Dashboard'],
  'profile': ['Profile Settings'],
  'college-settings': ['College Settings'],
  'consultants': ['Consultant'],
  'seat-booking': ['Seat Booking'],
  'admission': ['Admission'],
  'fees': ['Fees Management'],
  'accounts': ['Accounts'],
  'reports': ['Reports'],
  'attendance': ['Student Attendance'],
  'routine': ['Routine Management'],
  'examination': ['Examination'],
  'hr': ['HR Management'],
  'leave': ['Leave Manager'],
  'library': ['Library Management'],
  'inventory': ['Inventory'],
  'hostel': ['Hostel Management'],
  'transport': ['Transport'],
  'communication': ['Communication'],
  'transcripts': ['Transcripts'],
  'tickets': ['Ticket Management'],
  'user-management': ['User Management'],
  'roles': ['Role Management'],
  'master-settings': ['Master Settings'],
  'frontdesk': ['Front Desk'],
  'cash-register': ['Cash Register'],
  'bank-register': ['Bank Register'],
  'fee-collection': ['Fees Management'],
  'placement': ['Placement Cell'],
  'lms': ['LMS']
};

// Role to accessible modules mapping
export const ROLE_MODULES = {
  'Admin': ['*'], // All modules
  'Admission Officer': ['dashboard', 'profile', 'admission', 'seat-booking', 'consultants'],
  'Accountant': ['dashboard', 'profile', 'fees', 'accounts', 'cash-register', 'bank-register', 'fee-collection'],
  'HR Manager': ['dashboard', 'profile', 'hr', 'leave'],
  'Teacher': ['dashboard', 'profile', 'staff-portal', 'lms'],
  'Librarian': ['dashboard', 'profile', 'library'],
  'Front Desk': ['dashboard', 'profile', 'frontdesk'],
  'Exam Controller': ['dashboard', 'profile', 'examination', 'routine', 'transcripts'],
  'Inventory Manager': ['dashboard', 'profile', 'inventory'],
  'Hostel In-charge': ['dashboard', 'profile', 'hostel'],
  'Transport In-charge': ['dashboard', 'profile', 'transport']
};

export const useModuleAccess = () => {
  const { user } = useAuth();
  const [enabledModules, setEnabledModules] = useState([...FIXED_MODULES]);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [roleBasedModules, setRoleBasedModules] = useState([]);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await tenantsAPI.getMyModules();
        if (response.data) {
          setEnabledModules(response.data.enabled_modules || FIXED_MODULES);
          setIsSuperAdmin(response.data.is_superadmin || false);
        }
      } catch (error) {
        console.error('Failed to fetch modules:', error);
        setEnabledModules([...FIXED_MODULES]);
      } finally {
        setLoading(false);
      }
    };

    fetchModules();
  }, []);

  // Calculate role-based modules when user changes
  useEffect(() => {
    if (user) {
      const roles = user.roles || [user.role];
      setUserRoles(roles);
      
      // Calculate accessible modules based on roles
      const modules = new Set();
      roles.forEach(role => {
        const roleModules = ROLE_MODULES[role] || [];
        if (roleModules.includes('*')) {
          // Admin - add all modules
          [...FIXED_MODULES, ...CONFIGURABLE_MODULES].forEach(m => modules.add(m));
        } else {
          roleModules.forEach(m => modules.add(m));
        }
      });
      
      setRoleBasedModules(Array.from(modules));
    }
  }, [user]);

  // Check if user is Admin
  const isAdmin = () => {
    if (isSuperAdmin) return true;
    if (user?.role === 'Admin') return true;
    if (userRoles.includes('Admin')) return true;
    return false;
  };

  // Check if a module is enabled (considering both tenant modules and role-based access)
  const isModuleEnabled = (moduleCode) => {
    if (isSuperAdmin) return true;

    // Tenant-level module check applies to EVERYONE including Admin
    if (!enabledModules.includes(moduleCode) && !FIXED_MODULES.includes(moduleCode)) {
      return false;
    }

    // Admin bypasses only the role-based restriction, not tenant module access
    if (isAdmin()) return true;

    // Then check if user's role has access
    return roleBasedModules.includes(moduleCode);
  };

  // Check if a menu item should be visible
  const isMenuVisible = (menuLabel) => {
    if (isSuperAdmin) return true;

    // Find which module this menu belongs to
    for (const [moduleCode, menuLabels] of Object.entries(MODULE_MENU_MAPPING)) {
      if (menuLabels.includes(menuLabel)) {
        return isModuleEnabled(moduleCode);
      }
    }

    // Unmapped menus (admin utilities like Timezone/Backup) — visible to Admin only
    return isAdmin();
  };

  return {
    enabledModules,
    roleBasedModules,
    userRoles,
    loading,
    isSuperAdmin,
    isAdmin: isAdmin(),
    isModuleEnabled,
    isMenuVisible,
    FIXED_MODULES,
    CONFIGURABLE_MODULES
  };
};

export default useModuleAccess;
